import type { CurrentUser } from '@/entities/user/model/types';

/**
 * Qui administre quoi — la règle, en un seul endroit.
 *
 * L'en-tête et la garde de route posent la même question et doivent y répondre pareil : le
 * bouton « Administration » ne se montre qu'à qui a quelque chose derrière, et l'adresse
 * tapée à la main est refusée à qui n'a rien. IDALL-4192 est né de ces deux réponses
 * divergentes — le bouton n'en donnait aucune, et la route non plus.
 *
 * La donnée vient de `GET /users/me` : `tenants[].admin` dit les organisations administrées,
 * `administration.superAdmin` dit l'administrateur de plateforme, qui les atteint toutes.
 */
export function administersAnyTenant(user: CurrentUser | null | undefined): boolean {
  if (!user) return false;
  return user.administration?.superAdmin === true || user.tenants.some((tenant) => tenant.admin);
}

/** Les projets qu'on administre — un titre qui ne se déduit d'aucun autre. */
export function administeredProjects(user: CurrentUser | null | undefined) {
  return user?.administration?.projects ?? [];
}

/**
 * Y a-t-il quelque chose derrière le bouton « Administration » ?
 *
 * Une organisation administrée, ou un seul projet : le chef de chantier à qui on a confié
 * un projet et rien d'autre a lui aussi une administration, et c'est la sienne.
 */
export function administersAnything(user: CurrentUser | null | undefined): boolean {
  return administersAnyTenant(user) || administeredProjects(user).length > 0;
}

/** Ce projet-là, nommément. */
export function administersProject(
  user: CurrentUser | null | undefined,
  projectId: string | undefined
): boolean {
  if (!user || !projectId) return false;
  if (user.administration?.superAdmin === true) return true;
  return administeredProjects(user).some((project) => project.id === projectId);
}

/**
 * La même question, pour une organisation nommée.
 *
 * Administrer quelque part ne donne rien ailleurs : c'est la règle du backend
 * (`TenantPermissionEvaluator`), et la garde de route ne doit pas être plus permissive
 * qu'elle — sinon l'écran s'ouvre pour montrer des erreurs.
 */
export function administersTenant(
  user: CurrentUser | null | undefined,
  tenantId: string | undefined
): boolean {
  if (!user) return false;
  if (user.administration?.superAdmin === true) return true;
  if (!tenantId) return false;
  return user.tenants.some((tenant) => tenant.admin && tenant.id === tenantId);
}
